import { useMemo } from 'react';
import { raporBul } from '../../utils/raporSonek';
import type { PanelScreen, ParameterConfig, ReportConfig } from '../../types/panelConfig';

/** Bir sorgu kaynagi: ds id'si + bildirdigi parametreler (parametre gonderme karari buradan). */
export interface SaKaynak {
  sourceId: number;
  name: string;
  parameters: ParameterConfig[];
}

/**
 * Firma 7 `satis-analiz` config `kaynaklar` anahtarlari: SQL 139 (990051-56), SQL 141 (990057-58),
 * SQL 142 (menuden gizlenen 990040/042/014/017).
 */
export type EkranKaynakAnahtari =
  | 'kategori' | 'gunlukKar' | 'enCokSatan' | 'zararinaSatis' | 'iadeOrani' | 'urunKarliligi'
  | 'urunGunluk' | 'urunFisleri'
  | 'kanal040' | 'isi042' | 'cokSatan014' | 'subeKategori017';

export interface SatisKaynaklari {
  kanal040?: SaKaynak;
  saatlik002?: SaKaynak;
  isi042?: SaKaynak;
  sepet019?: SaKaynak;
  cokSatan014?: SaKaynak;
  kritik025?: SaKaynak;
  subeKategori017?: SaKaynak;
  kategori?: SaKaynak;
  gunlukKar?: SaKaynak;
  enCokSatan?: SaKaynak;
  zararinaSatis?: SaKaynak;
  iadeOrani?: SaKaynak;
  urunKarliligi?: SaKaynak;
  urunGunluk?: SaKaynak;
  urunFisleri?: SaKaynak;
}

/** Menude HIC olmayan SA ds'leri: yalniz ekran sozlugunden. */
const YALNIZ_EKRAN: EkranKaynakAnahtari[] = [
  'kategori', 'gunlukKar', 'enCokSatan', 'zararinaSatis', 'iadeOrani', 'urunKarliligi', 'urunGunluk', 'urunFisleri',
];

/**
 * Menude KALAN ds. `beklenenAd` ZORUNLU: sonek carpismasinda (051-070 firma 7'de pasif ittihat
 * sablonlariyla dolu; 035'te baska firmada "Acik Hesaplar") blok yanlis veri basmak yerine gizlenir.
 */
export function menuKaynagi(reports: ReportConfig[], sonek: number, beklenenAd: string): SaKaynak | undefined {
  const r = raporBul(reports, sonek, beklenenAd);
  if (!r || r.sourceId === null || r.sourceId === undefined) return undefined;
  return { sourceId: r.sourceId, name: r.name, parameters: r.parameters ?? [] };
}

/** Ekrana ozel (menude gizli olabilen) ds: API ekran `kaynaklar` sozlugunu rol izniyle cozer. */
export function ekranKaynagi(screen: PanelScreen, anahtar: EkranKaynakAnahtari): SaKaynak | undefined {
  const k = screen.kaynaklar?.[anahtar];
  if (!k || typeof k.sourceId !== 'number') return undefined;
  return { sourceId: k.sourceId, name: k.name, parameters: k.parameters ?? [] };
}

export function satisKaynaklari(screen: PanelScreen, reports: ReportConfig[]): SatisKaynaklari {
  const sonuc: SatisKaynaklari = {
    // G2b (spec §6.3): SQL 142 bu dort ds'i menuden gizler ve AYNI tx'te ekran sozlugune yazar. Once ekran;
    // yoksa menu (142 henuz inmedi) — panel SQL'den once ya da sonra deploy olsa da ekran bosalmaz.
    kanal040: ekranKaynagi(screen, 'kanal040') ?? menuKaynagi(reports, 40, 'Satış Kanalı Özeti'),
    saatlik002: menuKaynagi(reports, 2, 'Saatlik Satış Dağılımı'),
    isi042: ekranKaynagi(screen, 'isi042') ?? menuKaynagi(reports, 42, 'Saat × Gün Dağılımı'),
    sepet019: menuKaynagi(reports, 19, 'Birlikte Satılan Ürünler (son 90 gün)'),
    cokSatan014: ekranKaynagi(screen, 'cokSatan014') ?? menuKaynagi(reports, 14, 'En Çok Satan Ürünler'),
    kritik025: menuKaynagi(reports, 25, 'Kritik Stok'),
    subeKategori017: ekranKaynagi(screen, 'subeKategori017') ?? menuKaynagi(reports, 17, 'Şube × Kategori Satış'),
  };
  for (const a of YALNIZ_EKRAN) sonuc[a] = ekranKaynagi(screen, a);
  return sonuc;
}

export function useSatisKaynaklari(screen: PanelScreen, reports: ReportConfig[]): SatisKaynaklari {
  return useMemo(() => satisKaynaklari(screen, reports), [screen, reports]);
}
